import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {
  CourseAssignmentRequest,
  CourseAssignmentResponse,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {
  useGetCourseAssignmentQuery,
  useUpdateCourseAssignmentMutation,
} from 'src/redux/queries/courseAssignment';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CourseAssignmentPillConfig} from 'src/app/training/courseAssignmentPillConfig';
import {CourseAssignmentFormMeta} from '../../../../app/training/courseFormMeta';
import {CreateDefaultCourseAssignment} from '../../../../app/training/courseDefaults';
import {TrainingDocumentTitle} from '../../../../app/training/courseContentValidation';
import {TrainingIcon} from '../../../../app/training/coursePills';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const EditCourseDefinitionAssignmentPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, assignmentId} = useParams();
  const [data, setData] = useState<CourseAssignmentResponse>(
    CreateDefaultCourseAssignment(definitionId)
  );
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const {
    data: fetchedAssignment,
    isLoading,
    error: fetchError,
  } = useGetCourseAssignmentQuery(parseInt(assignmentId!), {
    skip: !assignmentId,
  });

  const [updateCourseAssignment] = useUpdateCourseAssignmentMutation();

  const form = useEntitySyncedForm({
    formMeta: CourseAssignmentFormMeta,
    entity: fetchedAssignment,
    onEntityArrival: setData,
    toFormData: toFormDataV2,
  });

  useEffect(() => {
    if (fetchError) {
      setAlert(parseErrorMessage(fetchError), 'error');
    }
  }, [fetchError]);

  const handleSave = async () => {
    if (!(await form.validateForm())) {
      return;
    }

    try {
      if (data === undefined || definitionId === undefined) {
        return;
      }

      setIsSavePending(true);

      const values = form.methods.getValues();
      const model: CourseAssignmentRequest = {
        id: values.id as number,
        assignedToId: values.assignedToId as number,
        courseDefinitionId: parseInt(definitionId),
        courseDefinitionVersion: values.courseDefinitionVersion as number,
        active: values.active as boolean,
        // Untouched date fields default to '' (not undefined) at form mount.
        assignedAt: (values.assignedAt || undefined) as Date,
        dueAt: (values.dueAt || undefined) as Date,
        completedAt: (values.completedAt || undefined) as Date,
      };

      await updateCourseAssignment(model).unwrap();

      setAlert(`Assignment was successfully updated.`, 'success');
      navigate(fallbackTo, {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'edit-assignment',
    definitionId,
    assignmentId,
    assigneeName: data.assignedTo?.name,
  });

  const pills = CourseAssignmentPillConfig(
    data?.state,
    data?.active,
    undefined,
    data?.dueAt
  );

  const isReady = !isLoading && !!fetchedAssignment;

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {fetchError ? (
          <Alert color="red" title="Error">
            Failed to load assignment. Please try again later.
          </Alert>
        ) : !isReady ? (
          <Spinner loadingText="Loading...." />
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={TrainingIcon}>
                {`Assignment for ${data.assignedTo?.name}`}
              </PageHeader.Title>
              {pills.length > 0 && (
                <PageHeader.Badges>
                  {pills.map((pill, index) => (
                    <PageHeader.Badge
                      key={index}
                      color={pill.color}
                      variant="light">
                      {pill.text}
                    </PageHeader.Badge>
                  ))}
                </PageHeader.Badges>
              )}
              <PageHeader.Actions>
                <PageHeader.Action
                  variant="save"
                  onClick={handleSave}
                  loading={isSavePending}>
                  Save
                </PageHeader.Action>
              </PageHeader.Actions>
            </PageHeader>
            <ProactForm form={form}>
              <ProactFormGrid />
            </ProactForm>

            <SaveButton loading={isSavePending} onClick={handleSave} />
          </>
        )}
      </Box>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.TrainingCourseAssignmentEdit(
    ':definitionId',
    ':assignmentId'
  ),
  element: <EditCourseDefinitionAssignmentPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.Update,
  isPrivate: true,
});
